import { AutogenLayoutCommodityXref } from "./autogen/AutogenLayoutCommodityXref";
import { ClickEvent, TableRowCreationEvent, TableContentsChangedEvent, DataSourceExecutionEvent, DataSourceAction, Label, LookupModelSelectionEvent, TableRow, Textbox, TableRowDisplayEvent, BlurEvent } from "@mcleod/components";
import { toggleExpandablePanel, updateXrefLabelFormatting, getFilteredRowCount, onBlurCrossReference } from "./DataFusionUtil";
import { ModelEdiCbpHts } from "./models/ModelEdiCbpHts";
import { StringUtil, DisplayType } from "@mcleod/core";
import { ModelRow } from "@mcleod/core";
import { CommonTooltips } from "@mcleod/common";

export class CommodityXref extends AutogenLayoutCommodityXref {
    private _panelCommodityExpanded: boolean = false;
    private labelCommodityCount: Label;
    private _mainLabelTitle: string = "Commodity";
    override async onLoad() {
        await super.onLoad();

        // Create and add the count label next to the main label
        this.createCountLabel();

        // Set initial label formatting and collapse panels by default
        this.updateLabelFormatting(true);
    }

    private createCountLabel() {
        // Create the count label
        this.labelCommodityCount = new Label({
            text: "",
            color: "primary",
            fontBold: true,
            rowBreak: false,
            marginBottom: 0,
            paddingBottom: 0,
            fontSize: "large"
        });

        // Find the parent container of the main label
        const labelParent = this.labelCommodity.parent;
        if (labelParent && this.horizontalspacerForCommodity) {
            // Insert the count label between the main label and the horizontal spacer
            const spacerIndex = labelParent.indexOf(this.horizontalspacerForCommodity);
            if (spacerIndex >= 0) {
                labelParent.insert(this.labelCommodityCount, spacerIndex);
            } else {
                // Fallback: add after the main label
                const labelIndex = labelParent.indexOf(this.labelCommodity);
                if (labelIndex >= 0) {
                    labelParent.insert(this.labelCommodityCount, labelIndex + 1);
                }
            }
        }
    }

    /** This is an event handler for the onClick event of buttonCommodityExpand. */
    async buttonCommodityExpandOnClick(event: ClickEvent) {
        this._panelCommodityExpanded = await toggleExpandablePanel(
            this._panelCommodityExpanded,
            this.panelCommodityCollapsible,
            this.buttonCommodityExpand
        );
    }

    /** This is an event handler for the onRowCreate event of tableCommodity. */
    tableCommodityOnRowCreate(event: TableRowCreationEvent) {
        console.log("Row create event triggered");

        if (this.owner && this.owner.mainDataSource && this.owner.mainDataSource.activeRow) {
            const parentRow = this.owner.mainDataSource.activeRow;
            const newRow = event.getTableRow().data;

            // Reset original data FIRST, before setting values, to prevent marking datasource as changed
            newRow.resetOriginalData();

            // Set the required fields from parent
            newRow.set("partner_id", parentRow.get("partner_id"));
            newRow.set("version", parentRow.get("version"));

            // Reset again after setting to clear the dirty flag
            newRow.resetOriginalData();

            // Force the datasource to recalculate its hasChanged state
            this.sourceEdiCommodity?.notifyHasChangedComponents(false);
        }

        // Update label formatting and panel state after row creation
        setTimeout(() => {
            this.updateLabelFormatting();
        }, 10);
    }

    /** This is an event handler for the afterExecution event of sourceEdiCommodity. */
    sourceEdiCommodityAfterExecution(event: DataSourceExecutionEvent) {
        // Only apply formatting after search operations and when we have data
        if (event.getAction() === DataSourceAction.SEARCH) {
            // Use setTimeout to ensure the formatting happens after the data is fully loaded
            setTimeout(() => {
                this.updateLabelFormatting();
            }, 100);
        }
    }

    private updateLabelFormatting(forceCollapse: boolean = false) {

        const rowCount = getFilteredRowCount(this.tableCommodity);

        // Set the main label text without count
        this.labelCommodity.text = this._mainLabelTitle;

        // Set the count label text and visibility
        if (this.labelCommodityCount) {
            this.labelCommodityCount.text = rowCount > 0 ? rowCount.toString() : "";
            this.labelCommodityCount.visible = rowCount > 0;
        }
        updateXrefLabelFormatting([
            {
                table: this.tableCommodity,
                label: this.labelCommodity,
                isExpanded: this._panelCommodityExpanded,
                collapsiblePanel: this.panelCommodityCollapsible,
                expandButton: this.buttonCommodityExpand,
                onStateChange: (newState: boolean) => {
                    this._panelCommodityExpanded = newState;
                }
            }
        ], forceCollapse);
    }

    /** This is an event handler for the onContentsChanged event of tableCommodity. */
    tableCommodityOnContentsChanged(event: TableContentsChangedEvent) {
        // Update label formatting when table contents change (add/delete rows)
        setTimeout(() => this.updateLabelFormatting(), 10);
    }

    /** This is an event handler for the onLookupModelSelection event of textboxCommodityId. */
    textboxCommodityIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection()) {
            const selectedRow = event.selection;
            const tableRow = TableRow.getContainingTableRow(event.target);

            if (tableRow) {
                // Set the revenue_description field when a new revenue code is selected
                tableRow.data.set("revenue_description", selectedRow.get("descr"));

                // Find and update the description textbox
                const textboxDescription = tableRow.findComponentById("textboxDescription") as Textbox;
                if (textboxDescription) {
                    textboxDescription.text = selectedRow.get("descr");
                }
            }
        }
    }

    async tableCommodityOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow: TableRow = event.getTableRow();
        const modelRow = tableRow.data;
        const textboxHts: Textbox = tableRow.findComponentById("textboxHts") as Textbox;
        const htsCode = modelRow.get("hts_code");

        // Setup tooltip using the extracted helper method
        await this.setupHtsTooltip(textboxHts, htsCode);
    }

    htsCodeOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection()) {
            const selectedRow = event.selection;
            const tableRow = TableRow.getContainingTableRow(event.target);

            if (tableRow) {
                const textboxHts: Textbox = tableRow.findComponentById("textboxHts") as Textbox;
                const htsCode = selectedRow.get("hts_no");

                // Setup tooltip using the extracted helper method
                this.setupHtsTooltip(textboxHts, htsCode);
            }
        }
    }

    /**
     * Sets up a comprehensive tooltip for HTS textbox with all available descriptions
     * @param textboxHts The textbox component to add the tooltip to
     * @param htsCode The HTS code to lookup descriptions for
     */
    private async setupHtsTooltip(textboxHts: Textbox, htsCode: string): Promise<void> {
        if (!StringUtil.isEmptyString(htsCode) && textboxHts) {
            const descriptions = await new ModelEdiCbpHts().search({hts_no: htsCode});
            if (descriptions?.modelRows.length > 0) {
                // Build tooltip components from all descriptions
                const tooltipItems = [];
                descriptions.modelRows.forEach((detailRow: ModelRow, index: number) => {
                    const description = detailRow.get("description");
                    if (description) {
                        tooltipItems.push({
                            caption: `Description ${index + 1}`,
                            displayType: DisplayType.STRING,
                            text: description
                        });
                    }
                });

                // Add the comprehensive tooltip to textboxHts
                if (tooltipItems.length > 0) {
                    CommonTooltips.setTooltipContentTextboxes(textboxHts, () => tooltipItems);
                }
            }
        }
    }

    async onBlurCommodity(event: BlurEvent) {
        await onBlurCrossReference(event, this.tableCommodity, "edi_commodity_id");
    }
}

